// Styles organized by component responsibility
export const composerStyles = {
    // Main wrapper component (visual container with borders, shadows, background)
    outerWrapper: {
        base: ['w-full', 'outline-none', 'flex', 'items-center', 'px-0', 'pb-0'],
        transition: 'duration-75 transition-[background-color,border-color,box-shadow]',
    },
    // Actual input elements (TextareaAutosize and ContentEditable)
    inputElement: [
        // Overflow and max height constraints
        'overflow-auto',
        'max-h-[45vh]',
        'lg:max-h-[40vh]',
        'sm:max-h-[25vh]',
        'outline-none',
        // Typography
        'font-sans',
        'resize-none',
        // Caret and selection
        'selection:bg-super-selection',
        'selection:text-primary',
        'dark:selection:text-super-primary',
        // Colors
        'text-primary',
        'bg-transparent',
        // Placeholder
        'placeholder:select-none',
    ],
    // Attribution components (left and right sidebars)
    attribution: {
        left: {
            base: ['gap-2', 'flex', 'items-center', 'overflow-hidden', 'min-w-0'],
            expanded: 'col-start-1 row-start-2',
            compact: 'order-0 ml-px',
        },
        right: {
            base: ['flex', 'items-center', 'justify-self-end', 'gap-2'],
            expanded: 'col-start-2 row-start-2',
            compact: 'order-2 mr-px',
        },
    },
    // Placeholder styles (for Lexical ContentEditable)
    placeholder: {
        base: ['absolute', 'inset-0', 'pointer-events-none', 'select-none', 'text-tertiary'],
        // The wrapper already supplies the input padding, so the compact placeholder
        // only needs to vertically center on the single fixed-height row, sit
        // flush-left with the typed text, and truncate instead of wrapping.
        compact: 'flex items-center overflow-hidden text-ellipsis whitespace-nowrap',
    },
};
export const framedComposerGeometry = {
    insetPx: 6,
    paddingClassName: 'p-1.5',
    // Reserve the same row for text, disclosures, and 32px icon actions. The frame owns vertical gaps.
    headerRowClassName: 'flex min-h-8 min-w-0 items-center px-4',
    // Tailwind needs literal classes; keep the outer and attached-menu radii together.
    frameRadiusClassName: 'rounded-[26px]',
    // The input box's `rounded-[20px]`, for inline-style overlays (dictation ring) on its edge.
    inputRadiusPx: 20,
    commandMenuBottomRadiusClassName: 'rounded-b-[20px]',
    dropdownAboveOverlapClassName: '-mb-1.5',
    dropdownBelowOverlapClassName: '-mt-1.5',
    // Undo the editor inset when suggestions attach to the full frame above it.
    dropdownAboveFrameClassName: '-mx-1.5 mb-1.5 w-[calc(100%+12px)] !rounded-t-[26px]',
    // Bleed the frame past the host column so the editor's edge lines up with sibling content.
    // Tailwind needs literal classes; keep both values in sync with `insetPx`.
    editorAlignmentClassName: '-mx-1.5 w-[calc(100%+12px)]',
    // Overlays that match the full frame extend past the host column with it.
    frameHorizontalInsetClassName: '-inset-x-1.5',
};
/** True for the padded-surround appearances (`framed`, `framed-accent`). */
export const isFramedAppearance = (appearance) => appearance === 'framed' || appearance === 'framed-accent';
/** The mask's surface in each color scheme. */
export const resolveComposerMask = (mask) => typeof mask === 'string' ? { light: mask, dark: mask } : mask;
// Tailwind needs literal classes.
const composerMaskFillClassNames = {
    base: ['bg-base', 'dark:bg-base'],
    raised: ['bg-raised', 'dark:bg-raised'],
};
/** Background classes that mask the element's rounded surface in each color scheme. */
export const getComposerMaskClassNames = (mask) => typeof mask === 'string'
    ? composerMaskFillClassNames[mask]
    : [composerMaskFillClassNames[mask.light][0], composerMaskFillClassNames[mask.dark][1]];
const boxAppearanceStyles = {
    raised: {
        border: 'border-base',
        activeBorder: 'border-focus',
    },
    inset: {
        // Concentric with the surrounding panel: its radius minus its padding.
        radius: 'rounded-[20px]',
        // The panel draws the outline; dark mode still needs a faint edge to split the two fills.
        border: 'border-transparent dark:border-white/15',
        fill: 'bg-raised dark:bg-black/30',
        shadow: 'shadow-[0_4px_10px_rgba(0,0,0,0.08)]',
    },
    flat: {
        border: 'border-soft',
        shadow: 'shadow-[0_0px_4px_rgba(0,0,0,0.03)]',
    },
};
/** Compose the radius, border, fill and ambient shadow for the given input state. */
export const getBoxAppearanceStyles = ({ appearance, isActive, hasTokenShadow = false, }) => {
    const style = boxAppearanceStyles[appearance];
    const border = hasTokenShadow
        ? 'forced-colors:border forced-colors:border-[CanvasText]'
        : `border ${(isActive && style.activeBorder) || style.border}`;
    return `${style.radius ?? 'rounded-2xl'} ${border} ${style.fill ?? 'bg-raised'} ${style.shadow ?? ''}`;
};
export const decorativeGlowStyles = {
    border: 'dglow-border',
    shadow: 'dglow-shadow',
    idleShadow: 'dglow-shadow-idle',
    dropdown: 'dglow-dropdown',
    idleDropdown: 'dglow-dropdown-idle',
    clipTop: 'dglow-clip-t',
    clipBottom: 'dglow-clip-b',
};
//# sourceMappingURL=styles.js.map